import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { API } from "../api";
import Layout from "../components/Layout";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Essentials,
  Paragraph,
  Heading,
  Bold,
  Italic,
  Underline,
  Font,
  Alignment,
  List,
  Link,
  BlockQuote,
  Table as CKTable,
  TableToolbar,
  Image as CKImage,
  ImageUpload,
  ImageToolbar,
  ImageStyle,
  ImageResize,
  SimpleUploadAdapter,
  HorizontalLine,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";

import { FiMail, FiSend } from "react-icons/fi";

import { toasts } from "../components/AppToast";

export default function SendMailRegularClient() {
  const navigate = useNavigate();
  const location = useLocation();

  const client = location.state?.client || null;

  const [languages, setLanguages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [mailContentLoading, setMailContentLoading] =
    useState(false);

  const [mailForm, setMailForm] = useState({
    client_name: "",
    client_id: "",
    email: "",
    lang_id: "",
    cont_title: "",
    cont_details: "",
  });

  // =========================================================
  // AUTH CHECK
  // =========================================================

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      navigate("/login", {
        replace: true,
      });
    }
  }, [navigate]);

  // =========================================================
  // CHECK CLIENT
  // =========================================================

  useEffect(() => {
    if (!client) {
      toasts.error("Client information not found");

      navigate("/manage-regular-clients", {
        replace: true,
      });
    }
  }, [client, navigate]);

  // =========================================================
  // FETCH LANGUAGES
  // =========================================================

  useEffect(() => {
    let mounted = true;

    const fetchLanguages = async () => {
      try {
        const res = await API.get("/languages");

        if (mounted) {
          setLanguages(
            Array.isArray(res.data?.data)
              ? res.data.data
              : []
          );
        }
      } catch (error) {
        console.error(
          "Failed to fetch languages:",
          error
        );

        if (mounted) {
          toasts.error(
            error.response?.data?.message ||
              "Failed to load languages"
          );
        }
      }
    };

    fetchLanguages();

    return () => {
      mounted = false;
    };
  }, []);

  // =========================================================
  // INITIALIZE MAIL FORM
  // =========================================================

  useEffect(() => {
    if (!client) return;

    const defaultLangId = "2";

    const clientName = [
      client.regclient_title,
      client.regclient_name,
      client.regclient_familyname,
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    setMailForm({
      client_name: clientName,
      client_id: client.client_id || "",
      email: client.regclient_email || "",
      lang_id: defaultLangId,
      cont_title: "",
      cont_details: "",
    });

    fetchMailContent(defaultLangId, client);
  }, [client]);

  // =========================================================
  // FETCH MAIL CONTENT
  // =========================================================

  const fetchMailContent = async (
    langId,
    selectedClient
  ) => {
    if (!langId || !selectedClient) {
      return;
    }

    setMailContentLoading(true);

    try {
      const response = await API.get(
        "/contents/mail-content",
        {
          params: {
            lang_id: langId,
          },
        }
      );

      const contentData =
        response.data?.data?.[0];

      let content =
        contentData?.cont_details || "";

      content = content
        .replace(
          /\[client_id\]/g,
          selectedClient.client_id || ""
        )
        .replace(
          /\[client_email\]/g,
          selectedClient.regclient_email || ""
        )
        .replace(
          /\[client_title\]/g,
          selectedClient.regclient_title || ""
        )
        .replace(
          /\[client_family_name\]/g,
          selectedClient.regclient_familyname || ""
        )
        .replace(
          /\[client_name\]/g,
          selectedClient.regclient_name || ""
        );

      setMailForm((prev) => ({
        ...prev,

        cont_title:
          contentData?.cont_title || "",

        cont_details: content,
      }));
    } catch (error) {
      console.error(
        "Mail content error:",
        error
      );

      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to load mail content"
      );
    } finally {
      setMailContentLoading(false);
    }
  };

  // =========================================================
  // LANGUAGE CHANGE
  // =========================================================

  const handleLanguageChange = async (e) => {
    const selectedLangId = e.target.value;

    setMailForm((prev) => ({
      ...prev,
      lang_id: selectedLangId,
      cont_title: "",
      cont_details: "",
    }));

    if (selectedLangId && client) {
      await fetchMailContent(
        selectedLangId,
        client
      );
    }
  };

  // =========================================================
  // SEND MAIL
  // =========================================================

  const handleSendMail = async () => {
    if (saving) {
      return;
    }

    // EMAIL VALIDATION
    if (!mailForm.email.trim()) {
      toasts.warning(
        "Email address is required"
      );
      return;
    }

    // LANGUAGE VALIDATION
    if (!mailForm.lang_id) {
      toasts.warning("Select language");
      return;
    }

    // SUBJECT VALIDATION
    if (!mailForm.cont_title.trim()) {
      toasts.warning("Enter subject");
      return;
    }

    // COMMENTS VALIDATION
    if (!mailForm.cont_details.trim()) {
      toasts.warning("Enter comments");
      return;
    }

    try {
      setSaving(true);

      const submitData = {
        client_id: mailForm.client_id,
        email: mailForm.email.trim(),
        lang_id: mailForm.lang_id,
        subject: mailForm.cont_title.trim(),
        comments: mailForm.cont_details,
      };

      await API.post(
        "/email/regularclients",
        submitData
      );

      // SUCCESS TOASTER
      toasts.success(
        "Mail sent successfully"
      );

      setTimeout(() => {
        navigate(
          "/manage-regular-clients"
        );
      }, 1000);
    } catch (error) {
      console.error(
        "Send mail error:",
        error
      );

      // ERROR TOASTER
      toasts.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to send mail"
      );
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // BACK
  // =========================================================

  const handleBack = () => {
    if (saving) {
      return;
    }

    navigate(
      "/manage-regular-clients"
    );
  };

  // =========================================================
  // RENDER
  // =========================================================

  if (!client) {
    return null;
  }

  return (
    <Layout activePage="Send Mail Regular Client">

      <main className="dashboard-main">

        <div className="user-form-page">

          {/* =================================================
              HEADER
          ================================================= */}

          <div className="user-form-header">

            <div className="d-flex align-items-center gap-2">

              <div className="form-card-icon">
                <FiMail size={24} />
              </div>

              <div>
                <h2 className="um-modal-title mb-0">
                  Send Mail
                </h2>

                <p className="form-card-sub mb-0">
                  Send an email to regular client
                </p>
              </div>

            </div>

            <button
              type="button"
              className="um-btn-secondary"
              onClick={handleBack}
              disabled={saving}
            >
              ← Back to Regular Clients
            </button>

          </div>

          {/* =================================================
              FORM CARD
          ================================================= */}

          <div className="user-form-card">

            {/* =================================================
                CLIENT INFORMATION
            ================================================= */}

            <div className="sec-div">
              <span>
                Client Mail Information
              </span>
            </div>

            <div className="row g-4">

              {/* CLIENT NAME */}

              <div className="col-xl-3 col-lg-3 col-md-6">

                <label className="um-label">
                  Client Name
                </label>

                <input
                  type="text"
                  className="um-input"
                  value={
                    mailForm.client_name || "—"
                  }
                  readOnly
                />

              </div>

              {/* CLIENT ID */}

              <div className="col-xl-3 col-lg-3 col-md-6">

                <label className="um-label">
                  Client ID
                </label>

                <input
                  type="text"
                  className="um-input"
                  value={
                    mailForm.client_id || "—"
                  }
                  readOnly
                />

              </div>

              {/* EMAIL */}

              <div className="col-xl-3 col-lg-3 col-md-6">

                <label className="um-label">
                  Email Address *
                </label>

                <input
                  type="email"
                  className="um-input"
                  value={mailForm.email}
                  readOnly
                />

              </div>

              {/* LANGUAGE */}

              <div className="col-xl-3 col-lg-3 col-md-6">

                <label className="um-label">
                  Language *
                </label>

                <select
                  className="um-select"
                  value={mailForm.lang_id}
                  disabled={
                    mailContentLoading ||
                    saving
                  }
                  onChange={
                    handleLanguageChange
                  }
                >

                  {languages.map(
                    (language) => (
                      <option
                        key={
                          language.lang_id
                        }
                        value={String(
                          language.lang_id
                        )}
                      >
                        {language.lang_name}
                      </option>
                    )
                  )}

                </select>

              </div>

            </div>

            {/* =================================================
                MAIL COMMENTS
            ================================================= */}

            <div className="sec-div mt-4 mb-3">
              <span>
                Mail Comments
              </span>
            </div>

            <div className="row g-4">

              {/* SUBJECT */}

              <div className="col-xl-12 col-lg-12 col-md-6">

                <label className="um-label">
                  Subject *
                </label>

                <input
                  type="text"
                  className="um-input"
                  value={
                    mailForm.cont_title
                  }
                  disabled={
                    mailContentLoading ||
                    saving
                  }
                  onChange={(e) =>
                    setMailForm(
                      (prev) => ({
                        ...prev,
                        cont_title:
                          e.target.value,
                      })
                    )
                  }
                />

              </div>

              {/* COMMENTS */}

              <div className="col-12">

                <label className="um-label">
                  Comments *
                </label>

                {mailContentLoading && (
                  <div className="text-muted mb-2">
                    Loading mail content...
                  </div>
                )}

                <div
                  className="season-discount-card"
                  style={{
                    overflow: "hidden",
                  }}
                >

                  <CKEditor
                    editor={ClassicEditor}
                    data={
                      mailForm.cont_details
                    }
                    disabled={
                      mailContentLoading ||
                      saving
                    }
                    config={{
                      licenseKey: "GPL",

                      plugins: [
                        Essentials,
                        Paragraph,
                        Heading,
                        Bold,
                        Italic,
                        Underline,
                        Font,
                        Alignment,
                        List,
                        Link,
                        BlockQuote,
                        CKTable,
                        TableToolbar,
                        CKImage,
                        ImageUpload,
                        ImageToolbar,
                        ImageStyle,
                        ImageResize,
                        SimpleUploadAdapter,
                        HorizontalLine,
                      ],

                      toolbar: [
                        "heading",
                        "|",
                        "fontFamily",
                        "fontSize",
                        "|",
                        "bold",
                        "italic",
                        "underline",
                        "|",
                        "fontColor",
                        "fontBackgroundColor",
                        "|",
                        "alignment",
                        "|",
                        "bulletedList",
                        "numberedList",
                        "|",
                        "link",
                        "blockQuote",
                        "insertTable",
                        "uploadImage",
                        "horizontalLine",
                        "|",
                        "undo",
                        "redo",
                      ],

                      fontFamily: {
                        options: [
                          "default",
                          "Arial, Helvetica, sans-serif",
                          "Georgia, serif",
                          "Times New Roman, Times, serif",
                          "Courier New, Courier, monospace",
                          "Verdana, Geneva, sans-serif",
                          "Tahoma, Geneva, sans-serif",
                          "Trebuchet MS, Helvetica, sans-serif",
                        ],

                        supportAllValues: true,
                      },

                      fontSize: {
                        options: [
                          9,
                          11,
                          13,
                          "default",
                          17,
                          19,
                          21,
                          24,
                          28,
                          32,
                        ],

                        supportAllValues: true,
                      },

                      table: {
                        contentToolbar: [
                          "tableColumn",
                          "tableRow",
                          "mergeTableCells",
                        ],
                      },

                      image: {
                        toolbar: [
                          "imageStyle:alignLeft",
                          "imageStyle:alignRight",
                          "imageStyle:block",
                          "|",
                          "toggleImageCaption",
                          "imageTextAlternative",
                          "resizeImage",
                        ],
                      },
                    }}

                    onChange={(
                      event,
                      editor
                    ) => {
                      const editorData =
                        editor.getData();

                      setMailForm(
                        (prev) => ({
                          ...prev,
                          cont_details:
                            editorData,
                        })
                      );
                    }}
                  />

                </div>

              </div>

            </div>

            {/* =================================================
                FOOTER
            ================================================= */}

            <div className="user-form-footer">

              <button
                type="button"
                className="um-btn-secondary"
                onClick={handleBack}
                disabled={saving}
              >
                Cancel
              </button>

              <button
                type="button"
                className="um-btn-primary"
                onClick={
                  handleSendMail
                }
                disabled={saving}
              >

                {saving ? (
                  <>
                    <span className="spinner" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FiSend />
                    Send Mail
                  </>
                )}

              </button>

            </div>

          </div>

        </div>

      </main>

    

    </Layout>
  );
}